import React from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FileText, Bot, Download, X } from "lucide-react";
import { cn } from "@/lib/utils";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkLatexSanitizer from '@/utils/remarkLatexSanitizer';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { sanitizeChatMarkdown } from '@/utils/chatMarkdownSanitizer';

const stripHtmlToText = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return (tempDiv.textContent || tempDiv.innerText || '').trim();
};

const buildStructuredMarkdown = (structuredContent) => {
    if (!structuredContent || !Array.isArray(structuredContent.sections) || structuredContent.sections.length === 0) {
        return '';
    }

    const lines = [];
    for (const section of structuredContent.sections) {
        if (section?.heading) {
            lines.push(`### ${section.heading}`);
        }
        if (Array.isArray(section?.points)) {
            for (const point of section.points) {
                if (point && String(point).trim()) {
                    lines.push(`- ${String(point).trim()}`);
                }
            }
        }
        lines.push('');
    }
    return lines.join('\n').trim();
};

const getNoteMarkdownForDisplay = (note) => {
    const structuredMd = buildStructuredMarkdown(note?.structured_content);
    if (structuredMd) return structuredMd;
    return stripHtmlToText(note?.content || '') || '';
};

export default function TeachersNotesPanel({
    isOpen,
    onClose,
    notes = [],
    isLoading,
    onAskBudleeAI
}) {
    if (!isOpen) return null;

    return (
        <div className="absolute inset-0 z-50 bg-white/95 backdrop-blur-sm flex flex-col animate-in fade-in slide-in-from-right-10 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-white shadow-sm">
                <div className="flex items-center gap-2">
                    <div className="p-2 bg-purple-100 rounded-lg">
                        <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                        <h2 className="font-bold text-lg text-gray-800">Teacher's Notes</h2>
                        <p className="text-xs text-gray-500">{notes.length} notes available</p>
                    </div>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full hover:bg-gray-100">
                    <X className="w-5 h-5 text-gray-500" />
                </Button>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1 p-4">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        <p className="text-sm text-gray-500">Loading notes...</p>
                    </div>
                ) : notes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <FileText className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-gray-900 font-medium mb-1">No notes yet</h3>
                        <p className="text-gray-500 text-sm max-w-[200px]">
                            Your teacher hasn't posted any notes for this chapter yet.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4 pb-4">
                        {notes.map((note) => (
                            <Card key={note.id} className="overflow-hidden border-purple-100 hover:border-purple-200 transition-colors group">
                                <div className="p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-semibold text-gray-800 line-clamp-2">{note.title || "Untitled Note"}</h3>
                                        {note.file_upload && (
                                            <span className="text-[10px] font-medium px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full border border-blue-100 shrink-0 ml-2">
                                                PDF
                                            </span>
                                        )}
                                    </div>

                                    <div className="text-sm text-gray-600 mb-4 line-clamp-3 prose prose-sm max-w-none">
                                        {note.content || (note.structured_content && note.structured_content.sections?.length > 0) ? (
                                            <ReactMarkdown
                                                remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                                rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false, trust: true }]]}
                                            >
                                                {sanitizeChatMarkdown(getNoteMarkdownForDisplay(note))}
                                            </ReactMarkdown>
                                        ) : (
                                            <span className="italic text-gray-400">No preview available</span>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 mt-2 pt-3 border-t border-gray-50">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1 gap-1.5 text-xs h-8 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                                            onClick={() => onAskBudleeAI(note.content, note.id)}
                                        >
                                            <Bot className="w-3.5 h-3.5" />
                                            Talk to Budlee
                                        </Button>

                                        {note.allow_download && note.file_path && (
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600">
                                                <a href={note.file_path} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full h-full">
                                                    <Download className="w-4 h-4" />
                                                </a>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </ScrollArea>
        </div>
    );
}
